/**
 * One athlete's simulation for one event, whatever the kind. This is the unit the server
 * runs authoritatively for every athlete and the client runs locally for prediction; both
 * feed it the same presses in event time (seconds since `refAt`: the gun for track events,
 * the event start for field events) and get identical states.
 */
import type { EventDef } from '../events.js';
import type { RunKey } from '../input.js';
import type { AthleteSnap } from '../protocol.js';
import {
  createHighJump,
  createLongJump,
  HIGH_JUMP,
  POLE_VAULT,
  highJumpAction,
  highJumpAdvance,
  highJumpNext,
  highJumpTimeout,
  jumpPress,
  LONG_JUMP,
  longJumpAction,
  longJumpAdvance,
  longJumpNext,
  longJumpTimeout,
  type HighJumpState,
  type LongJumpState,
} from './jump.js';
import {
  createRunner,
  runnerAdvance,
  runnerPress,
  runnerAction,
  SPRINT_100M,
  type RunnerConfig,
  type RunnerState,
} from './runner.js';
import {
  createThrow,
  SHOT_PUT,
  DISCUS,
  JAVELIN,
  throwAction,
  throwAdvance,
  throwNext,
  throwPress,
  throwTimeout,
  type ThrowConfig,
  type ThrowState,
} from './throw.js';

/** Pause after a landing or foul before the next attempt starts (s). */
export const ATTEMPT_PAUSE = 2.8;
/** Field events: an attempt not started within this long is a foul (s). */
export const ATTEMPT_CLOCK = 30;
/** Track events: not finished by then is a DNF (s after the gun). */
export const RUN_CAP = 120;
export function runCapFor(ev: EventDef): number {
  return ev.id === '800m' ? 300 : RUN_CAP;
}
/** Track events: how long before the gun the "On your marks" call comes (s). */
export const MARKS_LEAD = 4.2;

interface FieldCommon {
  attemptStart: number;
  nextAt: number | null;
  done: boolean;
}

export type AthleteSim =
  | { kind: 'run'; ev: EventDef; cfg: RunnerConfig; state: RunnerState; done: boolean }
  | ({ kind: 'lj'; ev: EventDef; cfg: typeof LONG_JUMP; state: LongJumpState } & FieldCommon)
  | ({ kind: 'hj'; ev: EventDef; cfg: typeof HIGH_JUMP; state: HighJumpState } & FieldCommon)
  | ({ kind: 'sp'; ev: EventDef; cfg: ThrowConfig; state: ThrowState } & FieldCommon);

export function runnerConfigFor(ev: EventDef): RunnerConfig {
  switch (ev.id) {
    case '400m':
      // Holding the sustainable 8/s all the way is ~49 s; a kick over the last 60 m gets under 47.
      return {
        ...SPRINT_100M,
        distance: 400,
        topSpeed: 10.2,
        stamina: { sustainable: 8, drainPerSec: 0.55, refillPerSec: 0.08, emptyFactor: 0.55 },
      };
    case '800m':
      return {
        ...SPRINT_100M,
        distance: 800,
        topSpeed: 8.4,
        accel: 1.3,
        stamina: { sustainable: 7, drainPerSec: 0.5, refillPerSec: 0.1, emptyFactor: 0.5 },
      };
    case '110h':
      return { ...SPRINT_100M, distance: 110, hurdles: { strict: false } };
    case '100m':
      return SPRINT_100M;
    default:
      throw new Error(`No running simulation for ${ev.id}`);
  }
}

export function createSim(
  ev: EventDef,
  opts: { startHeight?: number; strictHurdles?: boolean } = {},
): AthleteSim {
  if (ev.id === 'lj')
    return {
      kind: 'lj',
      ev,
      cfg: LONG_JUMP,
      state: createLongJump(),
      attemptStart: 0,
      nextAt: null,
      done: false,
    };
  if (ev.id === 'hj' || ev.id === 'pv')
    return {
      kind: 'hj',
      ev,
      cfg: ev.id === 'pv' ? POLE_VAULT : HIGH_JUMP,
      state: createHighJump(opts.startHeight ?? (ev.id === 'pv' ? 3.9 : 1.7)),
      attemptStart: 0,
      nextAt: null,
      done: false,
    };
  if (ev.id === 'sp' || ev.id === 'dt' || ev.id === 'jt')
    return {
      kind: 'sp',
      ev,
      cfg: ev.id === 'dt' ? DISCUS : ev.id === 'jt' ? JAVELIN : SHOT_PUT,
      state: createThrow(),
      attemptStart: 0,
      nextAt: null,
      done: false,
    };
  return {
    kind: 'run',
    ev,
    cfg: {
      ...runnerConfigFor(ev),
      ...(ev.id === '110h' ? { hurdles: { strict: opts.strictHurdles ?? false } } : {}),
    },
    state: createRunner(-MARKS_LEAD),
    done: false,
  };
}

/** The run-up runner of a field sim, if it has one. */
function fieldRunner(sim: AthleteSim): RunnerState | null {
  if (sim.kind === 'lj' || sim.kind === 'hj') return sim.state.runner;
  return null;
}

/** Has the athlete started this attempt (any press)? */
function attemptStarted(sim: AthleteSim): boolean {
  if (sim.kind === 'run') return true;
  if (sim.kind === 'sp') return sim.state.cadence.count > 0 || sim.state.phase !== 'power';
  const r = sim.state.runner;
  return r.cadence.count > 0 || r.pending.length > 0 || sim.state.phase !== 'runup';
}

export function simPress(sim: AthleteSim, key: RunKey, t: number): void {
  if (sim.done) return;
  if (sim.kind === 'run') {
    runnerPress(sim.state, sim.cfg, key, t);
    return;
  }
  if (sim.kind === 'sp') throwPress(sim.state, key, t - sim.attemptStart);
  else jumpPress(sim.state, sim.cfg, key, t - sim.attemptStart);
}

export function simAction(sim: AthleteSim, down: boolean, t: number): void {
  if (sim.done) return;
  if (sim.kind === 'run') {
    runnerAction(sim.state, sim.cfg, down, t);
    return;
  }
  const at = t - sim.attemptStart;
  if (sim.kind === 'lj') longJumpAction(sim.state, sim.cfg, down, at);
  else if (sim.kind === 'hj') highJumpAction(sim.state, sim.cfg, down, at);
  else throwAction(sim.state, sim.cfg, down, at);
  afterAttempt(sim);
}

/** After a landing/foul, schedule the next attempt from the sim's own clock. */
function afterAttempt(sim: AthleteSim): void {
  if (sim.kind === 'run') return;
  const p = sim.state.phase;
  if ((p === 'landed' || p === 'foul') && sim.nextAt === null)
    sim.nextAt = sim.attemptStart + sim.state.t + ATTEMPT_PAUSE;
}

function advanceField(sim: AthleteSim, at: number): void {
  if (sim.kind === 'lj') longJumpAdvance(sim.state, sim.cfg, at);
  else if (sim.kind === 'hj') highJumpAdvance(sim.state, sim.cfg, at);
  else if (sim.kind === 'sp') throwAdvance(sim.state, sim.cfg, at);
}

function timeoutField(sim: AthleteSim): void {
  if (sim.kind === 'lj') longJumpTimeout(sim.state);
  else if (sim.kind === 'hj') highJumpTimeout(sim.state);
  else if (sim.kind === 'sp') throwTimeout(sim.state);
}

function nextField(sim: AthleteSim): void {
  if (sim.kind === 'lj') longJumpNext(sim.state, sim.cfg);
  else if (sim.kind === 'hj') highJumpNext(sim.state, sim.cfg);
  else if (sim.kind === 'sp') throwNext(sim.state, sim.cfg);
}

/** Advance to event time `t`. */
export function simAdvance(sim: AthleteSim, t: number): void {
  if (sim.done) return;
  if (sim.kind === 'run') {
    runnerAdvance(sim.state, sim.cfg, t);
    if (sim.state.finished) sim.done = true;
    else if (t > runCapFor(sim.ev)) {
      sim.state.finished = true;
      sim.done = true;
    }
    return;
  }
  // Field: step attempt by attempt so a long `t` gap still plays out deterministically.
  for (let guard = 0; guard < 20; guard++) {
    if (sim.nextAt !== null) {
      if (t < sim.nextAt) return;
      nextField(sim);
      sim.attemptStart = sim.nextAt;
      sim.nextAt = null;
      if (sim.state.phase === 'done') {
        sim.done = true;
        return;
      }
    }
    const at = t - sim.attemptStart;
    // Attempt clock: never starting forfeits the attempt, exactly at the buzzer.
    if (!attemptStarted(sim) && at > ATTEMPT_CLOCK) {
      advanceField(sim, ATTEMPT_CLOCK);
      timeoutField(sim);
    } else {
      advanceField(sim, Math.min(at, 45));
      if (at > 45) timeoutField(sim);
    }
    afterAttempt(sim);
    if (sim.nextAt === null) return;
  }
}

/** Field events are played in turns: (re)base a fresh attempt's clock on event time `t`. */
export function simBeginTurn(sim: AthleteSim, t: number): void {
  if (sim.kind === 'run' || sim.done) return;
  if (!attemptStarted(sim)) {
    sim.attemptStart = t;
    sim.state.t = 0;
  }
}

/** True once the attempt has concluded and the pause is running. */
export function simTurnOver(sim: AthleteSim): boolean {
  return sim.kind !== 'run' && sim.nextAt !== null;
}

/** Event time at which the current turn's pause ends, or null. */
export function simTurnEndsAt(sim: AthleteSim): number | null {
  return sim.kind === 'run' ? null : sim.nextAt;
}

/** Close the current turn: move to the next attempt (or done) without running it. */
export function simFinishTurn(sim: AthleteSim): void {
  if (sim.kind === 'run' || sim.nextAt === null) return;
  nextField(sim);
  sim.attemptStart = sim.nextAt;
  sim.nextAt = null;
  if (sim.state.phase === 'done') sim.done = true;
}

/** Best mark so far (field) or finishing time (track). */
export function simMark(sim: AthleteSim): number | null {
  if (sim.kind === 'run') return sim.state.time;
  return sim.state.best;
}

export function simSnapshot(sim: AthleteSim, id: string): AthleteSnap {
  if (sim.kind === 'run') {
    const s = sim.state;
    return {
      id,
      x: s.x,
      y: s.jumpY,
      comY: 1 + s.jumpY,
      v: s.v,
      cadence: s.cadence.value,
      phase: s.finished ? 'finished' : s.t < 0 ? 'set' : 'running',
      angle: 0,
      power: 0,
      attempt: 0,
      mark: s.time,
      done: sim.done,
      bar: 0,
      misses: 0,
      results: [],
      falseStart: s.falseStart,
      hits: s.hits,
      hurdle: s.hurdle,
    };
  }
  if (sim.kind === 'sp') {
    const s = sim.state;
    return {
      id,
      runX: s.runX,
      runV: s.runV,
      heading: s.heading,
      projectilePitch: Math.atan2(s.vy, s.vx),
      x: s.shotX,
      y: s.shotY,
      comY: 1,
      v: s.flight && s.phase === 'flight' ? s.flight.vx : 0,
      cadence: s.cadence.value,
      phase: s.phase,
      angle: s.angle,
      power: s.power,
      attempt: s.attempt,
      mark: s.best,
      done: sim.done,
      bar: 0,
      misses: 0,
      results: s.results,
      falseStart: false,
    };
  }
  const s = sim.state;
  const r = fieldRunner(sim)!;
  return {
    id,
    x: s.x,
    y: s.y,
    comY: s.comY,
    v: s.phase === 'flight' && s.flight ? s.flight.vx : s.phase === 'runup' ? r.v : 0,
    cadence: r.cadence.value,
    phase: s.phase,
    angle: s.angle,
    power: 0,
    attempt: sim.kind === 'lj' ? sim.state.attempt : sim.state.log.length,
    mark: s.best,
    done: sim.done,
    bar: sim.kind === 'hj' ? sim.state.bar : 0,
    misses: sim.kind === 'hj' ? sim.state.misses : 0,
    results:
      sim.kind === 'lj'
        ? sim.state.results
        : sim.state.log.map((l) => (l.cleared ? l.height : null)),
    falseStart: false,
  };
}
